import { useState, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { useBoardSocket } from "../hooks/useBoardSocket"
import { LIMITS, getPlan } from "../lib/plans"
import { useParams, useNavigate, useSearch, Link } from "@tanstack/react-router"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { authClient } from "../lib/auth-client"
import { fetcher } from "../lib/api"
import { Button, Input, Skeleton } from "../components/ui"
import { ColorPicker } from "../components/color-picker"
import { CardDialog } from "../components/card-dialog"
import { BoardSettingsDialog } from "../components/board-settings-dialog"
import { LabelBadge } from "../components/label-badge"
import { Avatar } from "../components/avatar"
import { useToast } from "../components/toast"
import { Shell } from "../components/shell"
import { Plus, X, Calendar, MessageSquare, Paperclip, Trash2, MoreHorizontal, Pencil, Settings, CheckSquare, Filter, Tag, Lock } from "lucide-react"
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  type CollisionDetection,
  type DragStartEvent,
  type DragOverEvent,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"

type MemberUser = { id: string; name: string | null; email: string; image: string | null }


type ChecklistItemType = { id: string; text: string; checked: boolean; order: number }

type CardItem = {
  id: string
  title: string
  description: string | null
  priority: string | null
  startDate: string | null
  dueDate: string | null
  labels: string | null
  order: number
  columnId: string
  coverColor: string | null
  coverImage: string | null
  storyPoints?: number | null
  comments?: { id: string; content: string; user: { name: string | null } }[]
  attachments?: { id: string; fileName: string; fileUrl: string }[]
  assignees?: { user: MemberUser }[]
  checklistItems?: ChecklistItemType[]
}

type ColumnItem = {
  id: string
  name: string
  color: string | null
  order: number
  wipLimit: number | null
  cards: CardItem[]
}

/** Board page with the kanban columns and drag-and-drop cards. The most interactive page in the app, with live updates over websocket. */
export default function BoardPage() {
  const { workspaceId, boardId } = useParams({ from: "/workspaces/$workspaceId/projects/$projectId/boards/$boardId" })
  const search = useSearch({ strict: false }) as { openCard?: string }
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { data: session } = authClient.useSession()

  const [columns, setColumns] = useState<ColumnItem[]>([])
  const [activeColumn, setActiveColumn] = useState<ColumnItem | null>(null)
  const [activeCard, setActiveCard] = useState<CardItem | null>(null)
  const [cardOpen, setCardOpen] = useState(false)
  const [selectedCard, setSelectedCard] = useState<CardItem | null>(null)
  const [newColumn, setNewColumn] = useState("")
  const [addingColumn, setAddingColumn] = useState(false)
  const [addingColumnId, setAddingColumnId] = useState<string | null>(null)
  const [newCardTitle, setNewCardTitle] = useState("")
  const [editingColumnId, setEditingColumnId] = useState<string | null>(null)
  const [editColumnName, setEditColumnName] = useState("")
  const [columnMenuOpen, setColumnMenuOpen] = useState<string | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const [swimlanes, setSwimlanes] = useState(false)
  const [filterPriority, setFilterPriority] = useState("")
  const [filterAssignee, setFilterAssignee] = useState("")
  const [filterLabel, setFilterLabel] = useState("")
  const [filterDue, setFilterDue] = useState("")

  const { data: board, isLoading } = useQuery({
    queryKey: ["board", boardId],
    queryFn: () => fetcher<{ id: string; name: string; description: string | null; color: string | null; labelColors: Record<string, string> | null; projectId: string; columns: ColumnItem[] }>(`/api/boards/${boardId}`),
    enabled: !!session,
    refetchInterval: false,
  })

  useBoardSocket(boardId)

  const { data: members } = useQuery({
    queryKey: ["members", workspaceId],
    queryFn: () => fetcher<{ user: MemberUser }[]>(`/api/members?workspaceId=${workspaceId}`),
    enabled: !!session,
  })

  const { data: workspace } = useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: () => fetcher<{ role: string; plan: string }>(`/api/workspaces/${workspaceId}`),
    enabled: !!session,
  })
  const canTimeline = LIMITS[getPlan(workspace?.plan)].timeline


  useEffect(() => {
    if (board?.columns) setColumns(board.columns)
  }, [board])

  useEffect(() => {
    if (search.openCard && columns.length > 0) {
      const found = columns.flatMap((c) => c.cards).find((c) => c.id === search.openCard)
      if (found) { setSelectedCard(found); setCardOpen(true) }
    }
  }, [search.openCard, columns])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName
      const isInput = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT"
      if (e.key === "Escape") {
        if (cardOpen) { setCardOpen(false); return }
        if (settingsOpen) { setSettingsOpen(false); return }
        if (addingColumnId) { setAddingColumnId(null); setNewCardTitle(""); return }
        if (columnMenuOpen) { setColumnMenuOpen(null); return }
      }
      if (isInput) return
      if (e.key === "/") { e.preventDefault(); document.querySelector<HTMLInputElement>("[data-global-search]")?.focus() }
      if (e.key === "?") { e.preventDefault(); setShortcutsOpen((v) => !v) }
      if (e.key === "n" || e.key === "N") {
        if (columns.length > 0) {
          e.preventDefault()
          setAddingColumnId(columns[0].id)
        }
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [cardOpen, settingsOpen, addingColumnId, columnMenuOpen, columns])

  const exportCSV = () => {
    const rows: string[][] = [["Column", "Title", "Description", "Priority", "Assignee", "Due Date", "Labels"]]
    for (const col of columns) {
      for (const card of col.cards) {
        rows.push([
          col.name,
          card.title,
          card.description || "",
          card.priority || "",
          card.assignees?.length ? card.assignees.map((a) => a.user.name || a.user.email).join("; ") : "",
          card.dueDate ? new Date(card.dueDate).toLocaleDateString() : "",
          card.labels || "",
        ])
      }
    }
    const csv = rows.map((r) => r.map((v) => `"${v.replace(/"/g, '""')}"`).join(",")).join("\n")
    const blob = new Blob([csv], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `${board?.name || "board"}-export.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const [importOpen, setImportOpen] = useState(false)
  const [importRows, setImportRows] = useState<string[][]>([])
  const [importHeaders, setImportHeaders] = useState<string[]>([])
  const [importMapping, setImportMapping] = useState<Record<string, string>>({})
  const [importLoading, setImportLoading] = useState(false)

  const parseCSV = (text: string): string[][] => {
    const lines = text.split(/\r?\n/).filter(Boolean)
    return lines.map((line) => {
      const cells: string[] = []
      let cur = ""
      let inQ = false
      for (let i = 0; i < line.length; i++) {
        const ch = line[i]
        if (ch === '"') { if (inQ && line[i + 1] === '"') { cur += '"'; i++ } else inQ = !inQ }
        else if (ch === "," && !inQ) { cells.push(cur); cur = "" }
        else cur += ch
      }
      cells.push(cur)
      return cells
    })
  }

  const handleImportFile = (file: File) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const text = e.target?.result as string
      const rows = parseCSV(text)
      if (rows.length < 2) { toast("CSV has no data rows", "error"); return }
      setImportHeaders(rows[0])
      setImportRows(rows.slice(1))
      const autoMap: Record<string, string> = {}
      rows[0].forEach((h) => {
        const l = h.toLowerCase()
        if (l.includes("title") || l.includes("name") || l.includes("card")) autoMap["title"] = h
        if (l.includes("column") || l.includes("status") || l.includes("list")) autoMap["column"] = h
        if (l.includes("desc")) autoMap["description"] = h
        if (l.includes("priority")) autoMap["priority"] = h
        if (l.includes("due")) autoMap["dueDate"] = h
        if (l.includes("label") || l.includes("tag")) autoMap["labels"] = h
      })
      setImportMapping(autoMap)
      setImportOpen(true)
    }
    reader.readAsText(file)
  }

  const runImport = async () => {
    if (!importMapping["title"]) { toast("Map a Title column first", "error"); return }
    setImportLoading(true)
    try {
      const colNameToId = new Map(columns.map((c) => [c.name.toLowerCase(), c.id]))
      const firstColId = columns[0]?.id
      if (!firstColId) throw new Error("Board has no columns")
      const titleIdx = importHeaders.indexOf(importMapping["title"])
      const colIdx = importMapping["column"] ? importHeaders.indexOf(importMapping["column"]) : -1
      const descIdx = importMapping["description"] ? importHeaders.indexOf(importMapping["description"]) : -1
      const priIdx = importMapping["priority"] ? importHeaders.indexOf(importMapping["priority"]) : -1
      const dueIdx = importMapping["dueDate"] ? importHeaders.indexOf(importMapping["dueDate"]) : -1
      const lblIdx = importMapping["labels"] ? importHeaders.indexOf(importMapping["labels"]) : -1
      for (const row of importRows) {
        const title = row[titleIdx]?.trim()
        if (!title) continue
        const colName = colIdx >= 0 ? row[colIdx]?.trim().toLowerCase() : ""
        const columnId = (colName && colNameToId.get(colName)) || firstColId
        const body: any = { boardId, columnId, title }
        if (descIdx >= 0 && row[descIdx]) body.description = row[descIdx]
        if (priIdx >= 0 && row[priIdx]) body.priority = row[priIdx].toLowerCase()
        if (dueIdx >= 0 && row[dueIdx]) { const d = new Date(row[dueIdx]); if (!isNaN(d.getTime())) body.dueDate = d.toISOString() }
        if (lblIdx >= 0 && row[lblIdx]) body.labels = row[lblIdx]
        await fetcher("/api/cards", { method: "POST", body: JSON.stringify(body) })
      }
      await queryClient.invalidateQueries({ queryKey: ["board", boardId] })
      toast(`Imported ${importRows.length} cards`, "success")
      setImportOpen(false)
      setImportRows([])
    } catch (err: any) {
      toast(err.message || "Import failed", "error")
    } finally {
      setImportLoading(false)
    }
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const moveCard = useMutation({
    mutationFn: ({ cardId, columnId, order }: { cardId: string; columnId: string; order: number }) =>
      fetcher(`/api/cards/${cardId}`, { method: "PATCH", body: JSON.stringify({ columnId, order }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["board", boardId] }),
    onError: (err: any) => {
      toast(err.message || "Failed to move card", "error")
      if (board) setColumns(board.columns)
    },
  })

  const moveColumn = useMutation({
    mutationFn: ({ columnId, order }: { columnId: string; order: number }) =>
      fetcher(`/api/columns/${columnId}`, { method: "PATCH", body: JSON.stringify({ order }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["board", boardId] }),
    onError: (err: any) => {
      toast(err.message || "Failed to move column", "error")
      if (board) setColumns(board.columns)
    },
  })

  const createCard = useMutation({
    mutationFn: (body: { boardId: string; columnId: string; title: string }) =>
      fetcher("/api/cards", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["board", boardId] })
      toast("Card created", "success")
    },
    onError: (err: any) => toast(err.message || "Failed to create card", "error"),
  })

  const createColumn = useMutation({
    mutationFn: (body: { boardId: string; name: string }) =>
      fetcher("/api/columns", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["board", boardId] })
      setNewColumn("")
      toast("Column created", "success")
    },
    onError: (err: any) => toast(err.message || "Failed to create column", "error"),
  })

  const updateColumn = useMutation({
    mutationFn: ({ id, name, color, wipLimit }: { id: string; name?: string; color?: string | null; wipLimit?: number | null }) =>
      fetcher(`/api/columns/${id}`, { method: "PATCH", body: JSON.stringify({ name, color, wipLimit }) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["board", boardId] })
      toast("Column updated", "success")
    },
    onError: (err: any) => toast(err.message || "Failed to update column", "error"),
  })

  const deleteColumn = useMutation({
    mutationFn: (id: string) => fetcher(`/api/columns/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["board", boardId] })
      toast("Column deleted", "success")
    },
    onError: (err: any) => toast(err.message || "Failed to delete column", "error"),
  })

  if (!session) {
    navigate({ to: "/login" })
    return null
  }
  if (isLoading || !board) return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="sticky top-0 z-50 flex h-16 items-center justify-between border-b bg-card/80 px-4 shadow-sm backdrop-blur">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-8 w-8 rounded-md" />
      </div>
      <div className="flex flex-1 gap-4 overflow-x-auto p-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex w-72 shrink-0 flex-col rounded-xl border bg-card p-3 shadow-sm">
            <Skeleton className="mb-3 h-5 w-24" />
            <div className="space-y-2">
              <Skeleton className="h-20 w-full rounded-xl" />
              <Skeleton className="h-20 w-full rounded-xl" />
              <Skeleton className="h-20 w-full rounded-xl" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  const openCard = (card: CardItem) => {
    setSelectedCard(card)
    setCardOpen(true)
  }

  const handleDragStart = (event: DragStartEvent) => {
    const type = event.active.data.current?.type
    if (type === "Column") setActiveColumn(event.active.data.current?.column)
    if (type === "Card") setActiveCard(event.active.data.current?.card)
  }

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event
    if (!over) return
    const activeId = String(active.id)
    const overId = String(over.id)
    if (activeId === overId) return

    const activeType = active.data.current?.type
    const overType = over.data.current?.type
    if (activeType !== "Card") return

    setColumns((prev) => {
      const activeCol = prev.find((c) => c.cards.some((card) => card.id === activeId))
      if (!activeCol) return prev
      const activeIndex = activeCol.cards.findIndex((c) => c.id === activeId)
      const activeCardItem = activeCol.cards[activeIndex]

      const overColumn = overType === "Column" || overType === "ColumnCards" ? over.data.current?.column as ColumnItem : null
      const overCardId = overType === "Card" ? overId : null

      let overCol: ColumnItem | undefined
      let overIndex: number
      if (overCardId) {
        overCol = prev.find((c) => c.cards.some((card) => card.id === overCardId))
        if (!overCol) return prev
        overIndex = overCol.cards.findIndex((c) => c.id === overCardId)
      } else if (overColumn) {
        overCol = overColumn
        overIndex = overCol.cards.length
      } else {
        return prev
      }

      if (activeCol.id === overCol.id) {
        const newCards = arrayMove(activeCol.cards, activeIndex, overIndex)
        return prev.map((c) => (c.id === activeCol.id ? { ...c, cards: newCards } : c))
      }

      const newActiveCards = activeCol.cards.filter((c) => c.id !== activeId)
      const newOverCards = [...overCol.cards]
      newOverCards.splice(overIndex, 0, activeCardItem)
      return prev.map((c) => {
        if (c.id === activeCol.id) return { ...c, cards: newActiveCards }
        if (c.id === overCol.id) return { ...c, cards: newOverCards }
        return c
      })
    })
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setActiveColumn(null)
    setActiveCard(null)
    if (!over) return
    const activeId = String(active.id)
    const overId = String(over.id)
    if (activeId === overId) return

    const activeType = active.data.current?.type

    if (activeType === "Column") {
      const overColumnId = over.data.current?.type === "ColumnCards" ? (over.data.current?.column as ColumnItem)?.id : overId
      const oldIndex = columns.findIndex((c) => c.id === activeId)
      const newIndex = columns.findIndex((c) => c.id === overColumnId)
      if (oldIndex !== newIndex && newIndex !== -1) {
        const newColumns = arrayMove(columns, oldIndex, newIndex)
        setColumns(newColumns)
        newColumns.forEach((col, i) => {
          if (col.order !== i) moveColumn.mutate({ columnId: col.id, order: i })
        })
      }
      return
    }

    if (activeType === "Card") {
      const newCol = columns.find((c) => c.cards.some((card) => card.id === activeId))
      if (!newCol) return
      const newIndex = newCol.cards.findIndex((card) => card.id === activeId)
      moveCard.mutate({ cardId: activeId, columnId: newCol.id, order: newIndex })
    }
  }

  const isAdmin = workspace?.role === "owner" || workspace?.role === "admin"
  const activeType = activeCard ? "Card" : activeColumn ? "Column" : null
  const plan = getPlan(workspace?.plan)

  const allLabels = Array.from(new Set(
    columns.flatMap((col) => col.cards.flatMap((c) => (c.labels ? c.labels.split(",").map((l) => l.trim()).filter(Boolean) : [])))
  ))

  const hasFilters = !!(filterPriority || filterAssignee || filterLabel || filterDue)

  const filteredColumns = hasFilters
    ? columns.map((col) => ({
        ...col,
        cards: col.cards.filter((c) => {
          if (filterPriority && c.priority !== filterPriority) return false
          if (filterAssignee) {
            const assigneeIds = c.assignees?.length ? c.assignees.map((a) => a.user.id) : []
            if (!assigneeIds.includes(filterAssignee)) return false
          }
          if (filterLabel) {
            const cardLabels = c.labels ? c.labels.split(",").map((l) => l.trim()) : []
            if (!cardLabels.includes(filterLabel)) return false
          }
          if (filterDue) {
            const now = new Date()
            const weekAhead = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
            if (filterDue === "overdue") {
              if (!c.dueDate || new Date(c.dueDate) >= now) return false
            } else if (filterDue === "this_week") {
              if (!c.dueDate || new Date(c.dueDate) < now || new Date(c.dueDate) > weekAhead) return false
            } else if (filterDue === "none") {
              if (c.dueDate) return false
            }
          }
          return true
        }),
      }))
    : columns

  const collisionDetection: CollisionDetection = (args) => {
    const within = pointerWithin(args)
    if (within.length === 0) return closestCorners(args)
    const priority: Record<string, number> = { Card: 0, ColumnCards: 1, Column: 2 }
    return within.sort((a, b) => {
      const aType = a.data?.current?.type as string
      const bType = b.data?.current?.type as string
      return (priority[aType] ?? 99) - (priority[bType] ?? 99)
    })
  }

  return (
    <Shell
      title={board.name}
      back={{ to: "/workspaces/$workspaceId/projects/$projectId", label: "Project" }}
      boardColor={board.color}
      tabs={
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-sm">
            <span className="rounded-md bg-primary/10 px-3 py-1.5 font-medium text-primary">Board</span>
            <Link
              to="/workspaces/$workspaceId/projects/$projectId/boards/$boardId/list"
              params={{ workspaceId, projectId: board.projectId, boardId }}
              className="rounded-md px-3 py-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              List
            </Link>
            <Link
              to="/workspaces/$workspaceId/projects/$projectId/boards/$boardId/calendar"
              params={{ workspaceId, projectId: board.projectId, boardId }}
              className="rounded-md px-3 py-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              Calendar
            </Link>
            {canTimeline ? (
              <Link
                to="/workspaces/$workspaceId/projects/$projectId/boards/$boardId/timeline"
                params={{ workspaceId, projectId: board.projectId, boardId }}
                className="rounded-md px-3 py-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                Timeline
              </Link>
            ) : (
              <Link to="/workspaces/$workspaceId/billing" params={{ workspaceId }} className="flex items-center gap-1 rounded-md px-3 py-1.5 text-muted-foreground/50 hover:text-muted-foreground" title="Pro feature">
                Timeline <Lock className="h-3 w-3" />
              </Link>
            )}
          </div>
          <button onClick={() => setShortcutsOpen(true)} className="hidden items-center gap-2 text-xs text-muted-foreground sm:flex hover:text-foreground">
            <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">N</kbd> new card
            <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">/</kbd> search
            <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px]">?</kbd> shortcuts
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSwimlanes((v) => !v)}
              className={`rounded-md px-3 py-1.5 text-sm transition-colors ${swimlanes ? "bg-primary/10 font-medium text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
            >
              Swimlanes
            </button>
            <div className="h-5 w-px bg-border" />
            <Filter className="h-3.5 w-3.5 text-muted-foreground" />
            <div className="flex items-center gap-1 rounded-md border border-border bg-background px-2">
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="h-7 bg-transparent text-xs focus:outline-none"
              >
                <option value="">Priority</option>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div className="flex items-center gap-1 rounded-md border border-border bg-background px-2">
              <select
                value={filterAssignee}
                onChange={(e) => setFilterAssignee(e.target.value)}
                className="h-7 bg-transparent text-xs focus:outline-none"
              >
                <option value="">Assignee</option>
                {members?.map((m: any) => (
                  <option key={m.user.id} value={m.user.id}>{m.user.name || m.user.email}</option>
                ))}
              </select>
            </div>
            {allLabels.length > 0 && (
              <div className="flex items-center gap-1 rounded-md border border-border bg-background px-2">
                <Tag className="h-3 w-3 text-muted-foreground" />
                <select
                  value={filterLabel}
                  onChange={(e) => setFilterLabel(e.target.value)}
                  className="h-7 bg-transparent text-xs focus:outline-none"
                >
                  <option value="">Label</option>
                  {allLabels.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
            )}
            <div className="flex items-center gap-1 rounded-md border border-border bg-background px-2">
              <select
                value={filterDue}
                onChange={(e) => setFilterDue(e.target.value)}
                className="h-7 bg-transparent text-xs focus:outline-none"
              >
                <option value="">Due date</option>
                <option value="overdue">Overdue</option>
                <option value="this_week">Due this week</option>
                <option value="none">No due date</option>
              </select>
            </div>
            {hasFilters && (
              <button onClick={() => { setFilterPriority(""); setFilterAssignee(""); setFilterLabel(""); setFilterDue("") }} className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground">
                <X className="h-3 w-3" /> Clear
              </button>
            )}
          </div>
        </div>
      }
      actions={
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={exportCSV} title="Export as CSV">
            Export CSV
          </Button>
          {plan !== "free" ? (
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium transition-colors hover:bg-muted" title="Import from CSV">
              Import CSV
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportFile(f); e.target.value = "" }} />
            </label>
          ) : (
            <Link to="/workspaces/$workspaceId/billing" params={{ workspaceId }} className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-border bg-muted/50 px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted" title="Upgrade to import CSV">
              <Lock className="h-3.5 w-3.5" />
              Import CSV
            </Link>
          )}
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={() => setSettingsOpen(true)}>
              <Settings className="mr-1 h-4 w-4" /> Settings
            </Button>
          )}
        </div>
      }
    >
      {swimlanes && (() => {
        const memberMap = new Map((members || []).map((m) => [m.user.id, m.user]))
        const allCards = filteredColumns.flatMap((col) => col.cards.map((c) => ({ ...c, colName: col.name })))
        const lanes: { label: string; userId: string | null; cards: typeof allCards }[] = []
        const seen = new Set<string | null>()
        for (const c of allCards) {
          const uid = c.assignees?.length ? c.assignees[0].user.id : null
          if (!seen.has(uid)) {
            seen.add(uid)
            lanes.push({ label: uid ? (memberMap.get(uid)?.name || memberMap.get(uid)?.email || uid) : "Unassigned", userId: uid, cards: [] })
          }
          lanes.find((l) => l.userId === uid)!.cards.push(c)
        }
        if (lanes.length === 0) lanes.push({ label: "Unassigned", userId: null, cards: [] })
        return (
          <div className="flex-1 overflow-auto p-3 sm:p-6" style={board?.color ? { background: board.color } : {}}>
            <div className="space-y-4">
              {lanes.map((lane) => (
                <div key={lane.label} className="rounded-xl border border-border bg-card/60 p-3">
                  <div className="mb-2 flex items-center gap-2 px-1">
                    <Avatar id={lane.label} name={lane.label} size="sm" />
                    <span className="text-sm font-semibold">{lane.label}</span>
                    <span className="rounded-full bg-muted px-1.5 text-xs text-muted-foreground">{lane.cards.length}</span>
                  </div>
                  <div className="flex gap-3 overflow-x-auto pb-1">
                    {filteredColumns.map((col) => {
                      const laneCards = lane.cards.filter((c) => c.columnId === col.id)
                      return (
                        <div key={col.id} className="flex w-52 shrink-0 flex-col gap-1.5 rounded-lg border border-border/60 bg-muted/30 p-2">
                          <p className="mb-1 px-0.5 text-xs font-medium text-muted-foreground">{col.name} ({laneCards.length})</p>
                          {laneCards.map((card) => (
                            <div
                              key={card.id}
                              onClick={() => openCard(card)}
                              className={`cursor-pointer overflow-hidden rounded-lg border bg-card p-2 text-xs shadow-sm hover:border-primary/50 hover:shadow-md ${card.dueDate && new Date(card.dueDate) < new Date() ? "border-red-400/60" : "border-border"}`}
                            >
                              {card.coverColor && <div className="mb-1.5 h-1.5 w-full rounded-sm" style={{ backgroundColor: card.coverColor }} />}
                              <p className="font-medium leading-snug">{card.title}</p>
                              {card.priority && <span className="mt-1 inline-block rounded bg-muted px-1 py-0.5 text-[10px] capitalize text-muted-foreground">{card.priority}</span>}
                            </div>
                          ))}
                          {laneCards.length === 0 && <p className="py-2 text-center text-[10px] text-muted-foreground/50">—</p>}
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      })()}

      {!swimlanes && <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <main
          className="flex flex-1 snap-x snap-mandatory gap-3 overflow-x-auto p-3 sm:gap-4 sm:p-6"
          style={board?.color ? { background: board.color } : { background: "" }}
          onScroll={() => setColumnMenuOpen(null)}
        >
          <SortableContext items={filteredColumns.map((c) => c.id)} strategy={horizontalListSortingStrategy}>
            {filteredColumns.map((column) => (
              <SortableColumn
                key={column.id}
                column={column}
                activeType={activeType}
                plan={plan}
                labelColors={board?.labelColors ?? null}
                onCardClick={openCard}
                onAdd={(columnId) => setAddingColumnId((prev) => (prev === columnId ? null : columnId))}
                adding={addingColumnId === column.id}
                newCardTitle={newCardTitle}
                setNewCardTitle={setNewCardTitle}
                onCreateCard={(columnId, title) =>
                  createCard.mutate({ boardId, columnId, title }, { onSuccess: () => { setAddingColumnId(null); setNewCardTitle("") } })
                }
                creatingCard={createCard.isPending}
                editing={editingColumnId === column.id}
                editName={editColumnName}
                setEditName={setEditColumnName}
                onStartEdit={(id, name) => { setEditingColumnId(id); setEditColumnName(name); setColumnMenuOpen(null) }}
                onSaveEdit={(id) => { updateColumn.mutate({ id, name: editColumnName.trim() }, { onSuccess: () => setEditingColumnId(null) }) }}
                onUpdateColor={(id, color) => updateColumn.mutate({ id, color })}
                onUpdateWipLimit={(id, limit) => updateColumn.mutate({ id, wipLimit: limit })}
                onCancelEdit={() => setEditingColumnId(null)}
                onDelete={(id) => deleteColumn.mutate(id)}
                deleting={deleteColumn.isPending}
                menuOpen={columnMenuOpen === column.id}
                setMenuOpen={(open) => setColumnMenuOpen(open ? column.id : null)}
              />
            ))}
          </SortableContext>

          {hasFilters && filteredColumns.every((c) => c.cards.length === 0) && columns.length > 0 && (
            <div className="flex flex-1 flex-col items-center justify-center text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-muted">
                <Filter className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="mb-1 text-lg font-semibold">No cards match your filters</h3>
              <p className="mb-4 text-sm text-muted-foreground">Try adjusting or clearing the filters above.</p>
              <button
                onClick={() => { setFilterPriority(""); setFilterAssignee(""); setFilterLabel("") }}
                className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-muted"
              >
                Clear filters
              </button>
            </div>
          )}

          {filteredColumns.length === 0 && columns.length === 0 && (
            <div className="flex flex-1 flex-col items-center justify-center text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-muted">
                <Plus className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="mb-1 text-lg font-semibold">This board is empty</h3>
              <p className="mb-4 max-w-sm text-sm text-muted-foreground">Add your first column to start organizing cards.</p>
              <button
                onClick={() => setAddingColumn(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
              >
                <Plus className="h-4 w-4" /> Add column
              </button>
            </div>
          )}

          {addingColumn ? (
            <div className="flex h-fit w-[85vw] shrink-0 flex-col gap-2 rounded-lg border bg-card p-3 shadow-sm sm:w-72">
              <Input
                placeholder="Column name"
                value={newColumn}
                autoFocus
                disabled={createColumn.isPending}
                onChange={(e) => setNewColumn(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newColumn.trim()) {
                    createColumn.mutate({ boardId, name: newColumn.trim() }, { onSuccess: () => { setNewColumn(""); setAddingColumn(false) } })
                  }
                  if (e.key === "Escape") { setNewColumn(""); setAddingColumn(false) }
                }}
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => newColumn.trim() && createColumn.mutate({ boardId, name: newColumn.trim() }, { onSuccess: () => { setNewColumn(""); setAddingColumn(false) } })}
                  isLoading={createColumn.isPending}
                >
                  Add column
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setNewColumn(""); setAddingColumn(false) }}>Cancel</Button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setAddingColumn(true)}
              className="flex h-full w-[85vw] shrink-0 items-start justify-center rounded-lg border border-dashed border-border bg-muted/50 p-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground sm:w-72"
            >
              <Plus className="mr-2 h-4 w-4" /> Add another column
            </button>
          )}
        </main>

        <DragOverlay>
          {activeColumn && (
            <div className="flex w-72 shrink-0 flex-col overflow-hidden rounded-xl border bg-card p-0 opacity-80 shadow-lg">
              <div className="flex items-center justify-between border-b bg-muted/50 px-3 py-2">
                <div className="flex items-center gap-2">
                  {activeColumn.color && <span className="h-3 w-3 rounded-full" style={{ backgroundColor: activeColumn.color }} />}
                  <h3 className="font-semibold">{activeColumn.name}</h3>
                </div>
              </div>
              <div className="flex flex-col gap-2 p-3">
                {activeColumn.cards.map((card) => (
                  <div key={card.id} className="rounded-md border bg-card p-3 shadow-sm">
                    <p className="text-sm font-medium">{card.title}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {activeCard && (
            <div className="w-72 rotate-3 overflow-hidden rounded-xl border border-border bg-card shadow-xl">
              <CardInner card={activeCard} labelColors={board?.labelColors ?? null} />
            </div>
          )}
        </DragOverlay>
      </DndContext>}

      <CardDialog
        open={cardOpen}
        onClose={() => setCardOpen(false)}
        cardId={selectedCard?.id ?? null}
        boardId={boardId}
        workspaceId={workspaceId}
      />

      <BoardSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        boardId={boardId}
        workspaceId={workspaceId}
      />

      {importOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setImportOpen(false)}>
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-semibold">Import CSV — map columns</h2>
              <button onClick={() => setImportOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
            </div>
            <p className="mb-3 text-xs text-muted-foreground">{importRows.length} data rows detected. Map your CSV headers to card fields.</p>
            <div className="space-y-2">
              {(["title", "column", "description", "priority", "dueDate", "labels"] as const).map((field) => (
                <div key={field} className="flex items-center gap-3">
                  <span className="w-24 shrink-0 text-sm font-medium capitalize">{field === "dueDate" ? "Due date" : field}</span>
                  <select
                    value={importMapping[field] || ""}
                    onChange={(e) => setImportMapping((m) => ({ ...m, [field]: e.target.value }))}
                    className="flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-sm focus:outline-none"
                  >
                    <option value="">(skip)</option>
                    {importHeaders.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
              <Button onClick={runImport} isLoading={importLoading} disabled={!importMapping["title"]}>Import {importRows.length} rows</Button>
            </div>
          </div>
        </div>
      )}

      {shortcutsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShortcutsOpen(false)}>
          <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-semibold">Keyboard shortcuts</h2>
              <button onClick={() => setShortcutsOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-2 text-sm">
              {([
                ["N", "Add card to first column"],
                ["/", "Focus global search"],
                ["Esc", "Close dialog / cancel"],
                ["?", "Toggle this shortcuts panel"],
              ] as [string, string][]).map(([key, desc]) => (
                <div key={key} className="flex items-center justify-between">
                  <span className="text-muted-foreground">{desc}</span>
                  <kbd className="rounded border border-border bg-muted px-2 py-0.5 font-mono text-xs">{key}</kbd>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Shell>
  )
}

function SortableColumn({
  column,
  activeType,
  plan,
  labelColors,
  onCardClick,
  onAdd,
  adding,
  newCardTitle,
  setNewCardTitle,
  onCreateCard,
  creatingCard,
  editing,
  editName,
  setEditName,
  onStartEdit,
  onSaveEdit,
  onUpdateColor,
  onCancelEdit,
  onDelete,
  deleting,
  menuOpen,
  setMenuOpen,
  onUpdateWipLimit,
}: {
  column: ColumnItem
  activeType: "Card" | "Column" | null
  plan: import("../lib/plans").Plan
  labelColors: Record<string, string> | null
  onCardClick: (card: CardItem) => void
  onAdd: (id: string) => void
  adding: boolean
  newCardTitle: string
  setNewCardTitle: (v: string) => void
  onCreateCard: (columnId: string, title: string) => void
  creatingCard: boolean
  editing: boolean
  editName: string
  setEditName: (v: string) => void
  onStartEdit: (id: string, name: string) => void
  onSaveEdit: (id: string) => void
  onUpdateColor: (id: string, color: string | null) => void
  onCancelEdit: () => void
  onDelete: (id: string) => void
  deleting: boolean
  menuOpen: boolean
  setMenuOpen: (open: boolean) => void
  onUpdateWipLimit: (id: string, limit: number | null) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
    data: { type: "Column", column },
    disabled: activeType === "Card",
  })
  const { setNodeRef: setCardsDropRef } = useDroppable({
    id: `${column.id}-cards`,
    data: { type: "ColumnCards", column },
    disabled: activeType === "Column",
  })
  const style = { transform: CSS.Translate.toString(transform), transition }
  const wipExceeded = column.wipLimit != null && column.cards.length > column.wipLimit
  const [wipInput, setWipInput] = useState<string | null>(null)
  const menuButtonRef = useRef<HTMLDivElement>(null)
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null)

  useEffect(() => {
    if (menuOpen && menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect()
      setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right })
    } else {
      setMenuPos(null)
    }
  }, [menuOpen])

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex h-full w-[85vw] shrink-0 flex-col overflow-hidden rounded-xl border bg-card shadow-sm sm:w-72 snap-start ${isDragging ? "opacity-40" : ""}`}
    >
      <div ref={setCardsDropRef} className="flex min-h-[80px] flex-1 flex-col">
        <div className="flex items-center justify-between border-b bg-muted/50 px-3 py-2" {...attributes} {...listeners} onPointerDown={(e) => { if ((e.target as HTMLElement).closest('[data-no-drag]')) e.stopPropagation() }}>
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {editing ? (
            <Input
              value={editName}
              autoFocus
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation()
                if (e.key === "Enter" && editName.trim()) onSaveEdit(column.id)
                if (e.key === "Escape") onCancelEdit()
              }}
              className="h-8"
            />
          ) : (
            <div className="flex min-w-0 items-center gap-2">
              {column.color && (
                <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: column.color }} />
              )}
              <h3 className="truncate font-semibold">{column.name}</h3>
            </div>
          )}
          <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
            wipExceeded ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" : "bg-muted text-muted-foreground"
          }`}>
            {column.cards.length}{column.wipLimit != null ? `/${column.wipLimit}` : ""}
          </span>
        </div>
        <div className="relative" data-no-drag ref={menuButtonRef}>
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen) }}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
          {menuOpen && menuPos && createPortal(
            <div
              className="fixed z-50 w-56 rounded-md border bg-background p-1 shadow-lg"
              style={{ top: menuPos.top, right: menuPos.right }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <button
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted"
                onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onStartEdit(column.id, column.name) }}
              >
                <Pencil className="h-3.5 w-3.5" /> Rename
              </button>
              <div className="border-y border-border px-2 py-2">
                <p className="mb-1.5 text-xs font-medium text-muted-foreground">Column color</p>
                <ColorPicker
                  plan={plan}
                  value={column.color}
                  onChange={(color) => onUpdateColor(column.id, color)}
                />
              </div>
              <div className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted" onPointerDown={(e) => e.stopPropagation()}>
                <CheckSquare className="h-3.5 w-3.5 shrink-0" />
                <span className="shrink-0">WIP limit</span>
                {wipInput !== null ? (
                  <input
                    autoFocus
                    type="number"
                    min="0"
                    value={wipInput}
                    onChange={(e) => setWipInput(e.target.value)}
                    onPointerDown={(e) => e.stopPropagation()}
                    onBlur={() => { onUpdateWipLimit(column.id, wipInput === "" ? null : Number(wipInput)); setWipInput(null); setMenuOpen(false) }}
                    onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") { onUpdateWipLimit(column.id, wipInput === "" ? null : Number(wipInput)); setWipInput(null); setMenuOpen(false) } if (e.key === "Escape") { setWipInput(null) } }}
                    className="ml-auto w-14 rounded border border-border bg-background px-1.5 py-0.5 text-xs"
                    placeholder="none"
                  />
                ) : (
                  <button className="ml-auto text-xs text-muted-foreground hover:text-foreground" onClick={(e) => { e.stopPropagation(); setWipInput(column.wipLimit != null ? String(column.wipLimit) : "") }}>
                    {column.wipLimit != null ? column.wipLimit : "Set"}
                  </button>
                )}
              </div>
              <button
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm text-destructive hover:bg-muted"
                onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onDelete(column.id) }}
                disabled={deleting}
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            </div>,
            document.body
          )}
        </div>
        </div>

        <div className="flex flex-1 flex-col gap-2 p-3">
          <SortableContext items={column.cards.map((c) => c.id)} strategy={verticalListSortingStrategy}>
            {column.cards.map((card) => (
              <SortableCard key={card.id} card={card} activeType={activeType} labelColors={labelColors} onClick={() => onCardClick(card)} />
            ))}
          </SortableContext>
        </div>

        {adding ? (
          <div className="flex flex-col gap-2 px-3 pb-3">
            <Input
              placeholder="Card title"
              value={newCardTitle}
              autoFocus
              onChange={(e) => setNewCardTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && newCardTitle.trim()) {
                  onCreateCard(column.id, newCardTitle.trim())
                }
                if (e.key === "Escape") { setNewCardTitle(""); onAdd(column.id) }
              }}
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={() => newCardTitle.trim() && onCreateCard(column.id, newCardTitle.trim())} isLoading={creatingCard}>Add</Button>
              <Button size="sm" variant="ghost" onClick={() => { setNewCardTitle(""); onAdd(column.id) }}>Cancel</Button>
            </div>
          </div>
        ) : (
          <Button variant="ghost" size="sm" className="mx-3 mb-3 justify-start text-muted-foreground hover:text-foreground" disabled={creatingCard} onClick={() => onAdd(column.id)}>
            <Plus className="mr-1 h-4 w-4" /> Add card
          </Button>
        )}
      </div>
    </div>
  )
}

function CardInner({ card, labelColors }: { card: CardItem; labelColors: Record<string, string> | null }) {
  return (
    <>
      {card.coverImage ? (
        <div className="h-24 w-full bg-cover bg-center" style={{ backgroundImage: `url(${card.coverImage})` }} />
      ) : card.coverColor ? (
        <div className="h-8 w-full" style={{ backgroundColor: card.coverColor }} />
      ) : null}
      <div className="p-3">
      {card.labels && (
        <div className="mb-1.5 flex flex-wrap gap-1">
          {card.labels.split(",").map((l) => l.trim()).filter(Boolean).map((label, i) => (
            <LabelBadge key={label} label={label} color={labelColors?.[label]} index={i} />
          ))}
        </div>
      )}
      <p className="text-sm font-medium">{card.title}</p>
      <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
        {card.priority && (
          <span className={`rounded px-1.5 py-0.5 text-xs font-medium capitalize ${
            card.priority === "urgent" ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" :
            card.priority === "high" ? "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300" :
            card.priority === "low" ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" :
            "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300"
          }`}>
            {card.priority}
          </span>
        )}
        {card.dueDate && (() => {
          const due = new Date(card.dueDate)
          const overdue = due < new Date()
          return (
            <span className={`flex items-center gap-1 ${overdue ? "text-red-500 dark:text-red-400" : ""}`}>
              <Calendar className="h-3 w-3" />
              <span>{due.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
            </span>
          )
        })()}
        {card.storyPoints != null && (
          <span className="flex items-center gap-0.5 rounded bg-muted px-1 py-0.5 text-[10px] font-semibold text-muted-foreground" title="Story points">{card.storyPoints}pt</span>
        )}
        {card.comments && card.comments.length > 0 && <MessageSquare className="h-3 w-3" />}
        {card.attachments && card.attachments.length > 0 && <Paperclip className="h-3 w-3" />}
        {card.checklistItems && card.checklistItems.length > 0 && (
          <span className="flex items-center gap-0.5">
            <CheckSquare className="h-3 w-3" />
            <span>{card.checklistItems.filter((i) => i.checked).length}/{card.checklistItems.length}</span>
          </span>
        )}
        {card.assignees?.length ? (
          <div className="ml-auto flex -space-x-1.5">
            {card.assignees.slice(0, 3).map((a) => {
              const u = a.user
              return (
                <Avatar key={u.id} id={u.id} name={u.name} email={u.email} image={u.image} size="xs" className="border border-background" title={u.name || u.email} />
              )
            })}
            {(card.assignees?.length ?? 0) > 3 && (
              <div className="flex h-5 w-5 items-center justify-center rounded-full border border-background bg-muted text-[9px] font-semibold text-muted-foreground">+{(card.assignees?.length ?? 0) - 3}</div>
            )}
          </div>
        ) : null}
      </div>
      </div>
    </>
  )
}

function SortableCard({ card, activeType, labelColors, onClick }: { card: CardItem; activeType: "Card" | "Column" | null; labelColors: Record<string, string> | null; onClick: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: card.id,
    data: { type: "Card", card },
    disabled: activeType === "Column",
  })
  const style = { transform: CSS.Translate.toString(transform), transition }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className={`cursor-grab overflow-hidden rounded-xl border bg-card shadow-sm transition-all duration-200 hover:scale-[1.02] hover:shadow-md active:cursor-grabbing ${isDragging ? "opacity-30" : ""} ${card.dueDate && new Date(card.dueDate) < new Date() ? "border-red-400 dark:border-red-600" : "border-border hover:border-primary/50"}`}
    >
      <CardInner card={card} labelColors={labelColors} />
    </div>
  )
}

